<template>
  <button
    class="bg-primary-100 dark:bg-primary-700 flex h-10 flex-row items-center justify-center gap-3 rounded-full px-3"
    @click="processSetCommandPaletteOpened(true)"
  >
    <FontAwesomeIcon :icon="['fal', 'search']" class="dark:text-gray-300" />
    <span class="text-gray-500 dark:text-white">{{ $t('global.search') }}</span>
    <span
      class="hidden text-gray-500 dark:text-gray-400 [.os-macos_&]:flex [.os-macos_&]:flex-row [.os-macos_&]:items-center [.os-macos_&]:gap-0.5"
    >
      <kbd>⌘</kbd> +
      <kbd>K</kbd>
    </span>
    <span
      class="hidden text-gray-500 not-[.os-macos_&]:flex not-[.os-macos_&]:flex-row not-[.os-macos_&]:items-center not-[.os-macos_&]:gap-1 dark:text-gray-400"
    >
      <kbd>Ctrl</kbd> +
      <kbd>K</kbd>
    </span>
  </button>

  <TransitionRoot :show="commandPaletteOpened" as="template" appear>
    <Dialog
      as="div"
      class="relative z-[500]"
      @close="processSetCommandPaletteOpened(false)"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-blue-100/75 transition-opacity dark:bg-black/75"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-[500] overflow-y-auto p-4 sm:p-6 md:p-20">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="divider-y mx-auto max-w-3xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all dark:rounded-md dark:border dark:bg-slate-800 dark:text-white"
          >
            <Combobox v-slot="{ activeOption }" v-model="selectedResult">
              <div class="flex flex-row px-4">
                <div class="flex flex-1 flex-row items-center gap-2">
                  <FontAwesomeIcon
                    v-if="loading"
                    :icon="['fal', 'spinner-third']"
                    class="pointer-events-none h-5 w-5 shrink-0 animate-spin text-slate-400"
                    aria-hidden="true"
                    spin
                  />
                  <FontAwesomeIcon
                    v-else
                    :icon="['fal', 'magnifying-glass']"
                    class="pointer-events-none h-5 w-5 shrink-0 text-slate-400"
                    aria-hidden="true"
                  />
                  <ComboboxInput
                    id="search-input"
                    class="h-12 flex-1 border-0 bg-transparent text-gray-800 placeholder-slate-300 focus:border-0 focus:outline-none sm:text-sm dark:text-white"
                    placeholder="Search..."
                    autocomplete="off"
                    :display-value="() => searchValue"
                    @change="searchValue = $event.target.value"
                    @blur="testBlur"
                    @focus="testFocus"
                  />
                </div>
                <div class="flex shrink-0 items-center">
                  <button
                    v-for="existingFilter in existingFilters"
                    :key="existingFilter.value"
                    class="size-7 h-full"
                    :class="{
                      'text-gray-300': !searchFilters.includes(
                        existingFilter.value
                      ),
                      'text-primary': searchFilters.includes(
                        existingFilter.value
                      )
                    }"
                    @click="clickFilter(existingFilter.value)"
                  >
                    <FontAwesomeIcon :icon="existingFilter.icon" size="lg" />
                  </button>
                </div>
              </div>
              <ComboboxOptions
                v-if="searchResult.length"
                class="flex divide-x divide-gray-200"
                as="div"
                static
                hold
              >
                <div
                  :class="[
                    'max-h-96 min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4',
                    activeOption && 'sm:h-96'
                  ]"
                >
                  <div
                    hold
                    class="-mx-2 flex flex-col gap-2 text-sm text-gray-700 dark:text-white"
                  >
                    <template v-for="result in searchResult" :key="result.id">
                      <div>
                        <h2 class="capitalize-first py-1 font-bold">
                          {{ $t('attributes.' + result.label) }}
                        </h2>
                        <ComboboxOption
                          v-for="option in result.results"
                          :key="option.id"
                          v-slot="{ active }"
                          :value="option"
                          as="template"
                        >
                          <div
                            :class="[
                              'group flex cursor-pointer flex-row items-center rounded-md p-2 text-ellipsis select-none',
                              active &&
                                'bg-gray-100 text-gray-900 dark:text-slate-700'
                            ]"
                          >
                            <span
                              class="inline-flex flex-1 flex-row items-center gap-x-2 truncate"
                            >
                              <span class="capitalize-first">
                                {{ option.title }}
                              </span>

                              <small
                                v-if="wikiCityOptionHasSubTitle(option, result)"
                                class="capitalize-firs ml-2 text-slate-600 dark:text-white"
                              >
                                {{ option.subtitle }}
                              </small>
                            </span>
                            <span
                              v-if="option.distance_in_kilometers"
                              class="bg-primary text-contrast-primary shrink-0 rounded px-1 text-xs"
                            >
                              {{ option.distance_in_kilometers }} km
                            </span>
                          </div>
                        </ComboboxOption>
                      </div>
                    </template>
                  </div>
                </div>
                <CommandPalettePreview
                  v-if="activeOption"
                  :active-option="activeOption"
                  @click-option="(option) => clickResult(option)"
                />
              </ComboboxOptions>

              <div
                v-if="searchValue !== '' && searchResult.length === 0"
                class="px-6 py-14 text-center text-sm sm:px-14"
              >
                <p class="mt-2 text-gray-500 dark:text-white">
                  {{ $t('global.no_result_found') }}
                </p>
              </div>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import apiClient from '@u/apiClient.js'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { debounce } from '@u/debounce.js'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import CommandPalettePreview from '@c/CommandPalette/component/CommandPalettePreview.vue'

export default {
  name: 'CommandPalette',
  components: {
    CommandPalettePreview,
    ComboboxOption,
    ComboboxOptions,
    ComboboxInput,
    Combobox,
    DialogPanel,
    TransitionChild,
    Dialog,
    TransitionRoot,
    FontAwesomeIcon
  },
  data() {
    return {
      longitude: null,
      latitude: null,
      loading: false,
      searchResult: [],
      abortController: null,
      searchFilters: ['dossier', 'invoicing'],
      selectedResult: null,
      clickFilterLock: false,
      existingFilters: [
        {
          value: 'dossier',
          icon: ['fal', 'folder']
        },
        {
          value: 'people',
          icon: ['fal', 'user']
        },
        {
          value: 'invoicing',
          icon: ['fal', 'euro-sign']
        },
        {
          value: 'city',
          icon: ['fal', 'building-columns']
        },
        {
          value: 'place',
          icon: ['fal', 'map-marker-alt']
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      commandPaletteOpened: 'globalSearch/getCommandPaletteOpened',
      dossierTypes: 'metadata/getMetadataDossierTypes'
    }),
    searchValue: {
      get() {
        return this.$store.state.globalSearch.searchValue
      },
      set(value) {
        this.$store.commit('globalSearch/setSearchValue', value)
      }
    },
    getSearchResult() {
      return (this.searchResult || []).map((res) => {
        if (this.isResultDossier(res)) {
          res.results = (res.results || []).map((subRes) => ({
            ...subRes,
            type: this.dossierType(subRes.type)
          }))
        }
        return res
      })
    },
    isMac() {
      return navigator.platform.toUpperCase().indexOf('MAC') >= 0
    }
  },
  watch: {
    searchValue(...args) {
      this.debouncedSearch(...args)
    },
    commandPaletteOpened(newValue, oldValue) {
      if (newValue === true && newValue !== oldValue) {
        this.clickFilterLock = false
      }
    },
    searchFilters: {
      handler(newValue) {
        this.debouncedSearch(this.searchValue)
        localStorage.setItem(
          'command-palette-filters',
          JSON.stringify(newValue)
        )
      },
      deep: true
    },
    selectedResult(newValue, oldValue) {
      if (!this.clickFilterLock && newValue !== oldValue && newValue !== null) {
        this.clickResult(newValue)
      }
    }
  },
  mounted() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude
          this.longitude = position.coords.longitude
        },
        (error) => {},
        { timeout: 10000 }
      )
    }
    const saved = localStorage.getItem('command-palette-filters')
    if (saved) {
      try {
        this.searchFilters = JSON.parse(saved)
      } catch (e) {}
    }
    window.addEventListener('keydown', this.handleKeyDown)
  },
  created() {
    this.debouncedSearch = debounce((newValue, oldValue) => {
      if (newValue && newValue.length >= 3) {
        this.loading = true
        if (this.abortController) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        apiClient
          .get(`search-global`, {
            signal: this.abortController.signal,
            params: {
              terms: newValue,
              latitude: this.latitude,
              longitude: this.longitude,
              search: this.searchFilters
            }
          })
          .then((response) => {
            this.searchResult = response.data
          })
          .catch((reason) => {
            // console.log(reason)
          })
          .finally((_) => (this.loading = false))
      } else if (!newValue || newValue.length === 0) {
        this.searchResult = []
        // this.processResetSearchResult()
      }
    }, 500)
  },
  beforeUnmount() {
    if (this.abortController) {
      this.abortController.abort()
    }
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    ...mapActions({
      processGetGlobalSearchResultAction: 'globalSearch/getGlobalSearchResult'
    }),
    ...mapMutations({
      processSetCommandPaletteOpened: 'globalSearch/setCommandPaletteOpened',
      processResetSearchResult: 'globalSearch/resetSearchResult',
      processSetTo: 'email/setTo',
      processSetIsModalOpen: 'email/setIsModalOpen',
      processResetEmail: 'email/resetEmail'
    }),
    isResultType(type, result) {
      return result.type === type
    },
    isResultDossier(result) {
      return this.isResultType('App\\Models\\Dossier', result)
    },
    wikiCityOptionHasSubTitle(option, result) {
      return (
        this.isResultType('App\\Models\\WikiCity', result) &&
        option.subtitle &&
        option.subtitle.length
      )
    },
    clickResult(result) {
      this.$router.push(result.router)
      this.processSetCommandPaletteOpened(false)
    },
    sendEmail(email) {
      this.processResetEmail()
      this.processSetTo(email.email)
      this.processSetIsModalOpen(true)
      this.processSetCommandPaletteOpened(false)
    },
    dossierType(type) {
      return this.dossierTypes?.find((dt) => dt.id === type) ?? ''
    },
    clickFilter(value) {
      this.clickFilterLock = false
      this.$h.toggleElementInArray(this.searchFilters, value)
      document.getElementById('search-input').focus()
    },
    testBlur() {
      this.clickFilterLock = true
    },
    testFocus() {
      this.clickFilterLock = false
    },
    handleKeyDown(e) {
      const isCorrectCombo =
        (this.isMac && e.metaKey && e.key === 'k') ||
        (!this.isMac && e.ctrlKey && e.key === 'k')
      if (isCorrectCombo) {
        e.preventDefault()
        this.processSetCommandPaletteOpened(true)
      }
    }
  }
}
</script>

<style></style>
